import api from './axiosInstance';

/**
 * Obtiene usuarios paginados del backend
 * @param {number} page - Número de página (comienza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {boolean} onlyActive - Si true, solo retorna usuarios activos
 * @param {string} search - Término de búsqueda (opcional)
 * @returns {Promise} - { page, pageSize, total, data }
 */
export async function getUsersPaged(
  page = 1,
  pageSize = 10,
  onlyActive = false,
  search = null,
  sortBy = null,
  desc = null
) {
  try {
    const params = {
      page,
      pageSize,
      onlyActive,
    };

    if (search && search.trim()) {
      params.search = search.trim();
    }

    if (sortBy) {
      params.sortBy = sortBy;
      if (desc !== null && desc !== undefined) {
        params.desc = desc;
      }
    }

    const response = await api.get('Users/paged', {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });

    return {
      page: response.data.page,
      pageSize: response.data.pageSize,
      total: response.data.total,
      users: response.data.data || [],
    };
  } catch (error) {
    console.error('getUsersPaged failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de usuarios.';
    throw error;
  }
}

export async function getSponsors() {
  try {
    const response = await api.get('Users/GetSponsors', {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data.data;
  } catch (error) {
    console.error('getSponsors failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de patrocinantes.';
    throw error;
  }
}

export async function getUserVenta(signal) {
  try {
    const response = await api.get('Users/getUserVenta', {
      headers: { 'Content-Type': 'application/json' },
      signal,
    });

    return response.data;
  } catch (error) {
    console.error('getUserVenta failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

/**
 * Obtiene todas las áreas de negocio
 * @returns {Promise<Array>}
 */
export async function getAllBusinessAreas() {
  try {
    const response = await api.get('BusinessAreaSaas/GetAllBusinessArea', {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data?.data || response.data || [];
  } catch (error) {
    console.error('getAllBusinessAreas failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de áreas de negocio.';
    throw error;
  }
}

export async function updateUserBusinessArea({
  userId,
  businessAreaId,
  updaterId,
}) {
  try {
    const payload = {
      userId,
      businessAreaId,
      updaterId,
    };

    const response = await api.patch(
      '/Users/admin/update-business-area',
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    error.customMessage =
      'No se pudo actualizar el área del usuario.';
    throw error;
  }
}

/**
 * Crea un nuevo usuario
 * @param {object} userData - Datos del usuario { name, email, phone, roleId, mfa, identifier, identifierTypeId, businessAreaId }
 * @returns {Promise} - El usuario creado
 */
export async function createUser(userData) {
  try {
    const payload = {
      name: userData.name || '',
      email: userData.email || '',
      phone: userData.phone || '',
      roleId: userData.roleId || 0,
      mfa: userData.mfa ?? false,
      identifier: userData.identifier || '',
      identifierTypeId: userData.identifierTypeId || 0,
      businessAreaId: userData.businessAreaId || 0,
      creator_Id: userData.creator_Id || 0,
    };

    const response = await api.post('Users/admin/create', payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('createUser failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

/**
 * Actualiza un usuario existente
 * @param {number} userId - ID del usuario
 * @param {object} userData - Datos actualizados { name, email, phone, roleId, mfa, identifier, identifierTypeId, businessAreaId, active }
 * @returns {Promise} - El usuario actualizado
 */
export async function updateUser(userId, userData) {
  try {
    const payload = {
      id: userId,
      name: userData.name || '',
      email: userData.email || '',
      phone: userData.phone || '',
      active: userData.active ?? true,
      roleId: userData.roleId || 0,
      mfa: userData.mfa ?? false,
      identifier: userData.identifier || '',
      identifierTypeId: userData.identifierTypeId || 0,
      businessAreaId: userData.businessAreaId || 0,
      updater_Id: userData.updater_Id || 0,
    };

    const response = await api.put('Users/admin/update', payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('updateUser failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

/**
 * Obtiene un usuario por ID con roles disponibles
 * @param {number} userId - ID del usuario
 * @returns {Promise<Object>} - Usuario con roles
 */
export async function getUserById(userId) {
  try {
    const response = await api.get(`Users/admin/${userId}`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('getUserById failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

/**
 * Elimina un usuario (borrado lógico)
 * @param {number} userId - ID del usuario a eliminar
 * @returns {Promise}
 */
export async function deleteUser(userId) {
  try {
    const response = await api.delete(`Users/${userId}`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('deleteUser failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

/**
 * Obtiene la lista de roles disponibles
 * @returns {Promise<Array>} - Array de roles
 */
export async function getRoles() {
  try {
    const response = await api.get('Users/admin/roles', {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('getRoles failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}
